/**
 * Shared Mapbox paint for the country policy overlay used by the main map
 * (App.tsx) and the dashboard map (DashboardCountryVaults).
 *
 * Borders come from `/api/countries` (the same countries.geojson edited in
 * Manage Country). Policy is shown as coloured outline lines; fill stays
 * transparent so the basemap remains readable when zoomed in.
 *
 * Outline geometry is NOT each polygon stroked separately (that draws every
 * shared border twice — two near-coincident lines that read as a doubled /
 * beaded edge, most visible on the large main-map globe). Instead we build a
 * TopoJSON topology, which detects and de-duplicates coincident border arcs,
 * and emit each arc exactly once from `country-borders`.
 */

import type { Map as MapboxMap, GeoJSONSource } from 'mapbox-gl';
import { topology } from 'topojson-server';
import { mesh } from 'topojson-client';

export const COUNTRIES_SOURCE_ID = 'countries';
export const COUNTRY_BORDERS_SOURCE_ID = 'country-borders';
export const COUNTRIES_FILL_LAYER_ID = 'countries-fill';
export const COUNTRIES_OUTLINE_LAYER_ID = 'countries-outline';

/**
 * Invisible fill — kept for hit-testing (click / hover) only.
 * fill-opacity 0 still participates in queryRenderedFeatures.
 *
 * Critical: disable fill antialias / outline. Mapbox still strokes
 * `fill-outline-color` (defaults to fill-color) even when fill-opacity is 0,
 * which double-drew every shared border beside our line layer.
 */
export const COUNTRIES_FILL_PAINT = {
  'fill-color': '#000000',
  'fill-opacity': 0,
  // Prevents Mapbox from stroking polygon edges via fill-outline
  // (still drawn when fill-opacity is 0 unless antialias is off).
  'fill-antialias': false,
  'fill-outline-color': 'rgba(0,0,0,0)',
};

/** Policy-coloured outline — green allowed / red denied / grey unknown. */
export const COUNTRIES_OUTLINE_PAINT = {
  'line-color': [
    'case',
    ['==', ['typeof', ['get', 'allowed']], 'boolean'],
    ['case', ['get', 'allowed'], '#00FF00', '#FF0000'],
    '#666666',
  ],
  // Keep modest — shared by main map and dashboard.
  'line-width': [
    'interpolate',
    ['linear'],
    ['zoom'],
    0,
    1.5,
    3,
    2,
    6,
    2.5,
  ],
  'line-opacity': 0.95,
};

export const COUNTRIES_OUTLINE_LAYOUT = {
  'line-join': 'round' as const,
  'line-cap': 'round' as const,
};

type AllowedProp = boolean | undefined;
type PolicyColor = 'allowed' | 'denied' | 'unknown';

/**
 * Decide a border arc's colour from the two countries that share it (interior
 * borders) or the single country (coastline, where both args are the same
 * geometry). Denied (red) wins over allowed (green) so a border between an
 * allowed and a denied country stays red; unknown falls back to grey.
 */
function borderColor(a: AllowedProp, b: AllowedProp): PolicyColor {
  if (a === false || b === false) return 'denied';
  if (a === true || b === true) return 'allowed';
  return 'unknown';
}

/**
 * Build a LineString/MultiLineString FeatureCollection with one stroke per
 * unique country border, coloured by policy.
 *
 * The Manage-Country dataset stores each country as an independent polygon, so
 * stroking every polygon draws each shared border twice. We convert to a
 * TopoJSON topology (which cuts rings at junctions and de-duplicates coincident
 * arcs) and then emit each arc once via `mesh`, filtered into three policy
 * colours. Each output feature carries an `allowed` property the outline paint
 * expression reads for its colour.
 */
/** Cache TopoJSON mesh results — rebuild is expensive (~countries × arcs). */
let borderLinesCache: {
  countries: GeoJSON.FeatureCollection;
  lines: GeoJSON.FeatureCollection;
} | null = null;

export function buildCountryBorderLines(
  countries: GeoJSON.FeatureCollection,
): GeoJSON.FeatureCollection {
  if (borderLinesCache?.countries === countries) {
    return borderLinesCache.lines;
  }

  const features = countries.features ?? [];
  if (features.length === 0) {
    const empty = { type: 'FeatureCollection' as const, features: [] };
    borderLinesCache = { countries, lines: empty };
    return empty;
  }

  // Quantisation snaps near-coincident shared borders so the topology emits
  // each once. 1e5 keeps ~sub-km precision (no visible blockiness) while still
  // collapsing the duplicate strokes.
  const topo = topology({ countries } as any, 1e5) as any;
  const object = topo.objects.countries;

  const allowedOf = (geom: any): AllowedProp => geom?.properties?.allowed;

  const meshFor = (want: PolicyColor): GeoJSON.MultiLineString =>
    mesh(topo, object, (a: any, b: any) =>
      borderColor(allowedOf(a), allowedOf(b)) === want,
    ) as GeoJSON.MultiLineString;

  const out: GeoJSON.Feature[] = [];
  const pushColor = (color: PolicyColor, allowed: AllowedProp) => {
    const geometry = meshFor(color);
    if (geometry.coordinates.length > 0) {
      out.push({ type: 'Feature', properties: { allowed }, geometry });
    }
  };

  pushColor('denied', false);
  pushColor('allowed', true);
  pushColor('unknown', undefined);

  const lines = { type: 'FeatureCollection' as const, features: out };
  borderLinesCache = { countries, lines };
  return lines;
}

/** Invalidate the TopoJSON mesh cache (call after policy colours change). */
export function invalidateCountryBorderCache(): void {
  borderLinesCache = null;
}

const EMPTY_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

/** Push unique-arc border linework into the `country-borders` source. */
export function syncCountryBorderSource(
  map: MapboxMap,
  countries: GeoJSON.FeatureCollection,
): void {
  const lines = buildCountryBorderLines(countries);
  const existing = map.getSource(COUNTRY_BORDERS_SOURCE_ID) as GeoJSONSource | undefined;
  if (existing) {
    existing.setData(lines);
  } else {
    map.addSource(COUNTRY_BORDERS_SOURCE_ID, {
      type: 'geojson',
      data: lines,
    });
  }
}

/**
 * Instantly re-attach countries + green/red border layers from an in-memory
 * FeatureCollection (no network). Used after basemap `setStyle` wipes custom
 * sources — style swaps must not wait on a refetch of /api/countries.
 */
export function reapplyCountryOverlay(
  map: MapboxMap,
  countries: GeoJSON.FeatureCollection,
): void {
  try {
    const existing = map.getSource(COUNTRIES_SOURCE_ID) as GeoJSONSource | undefined;
    if (existing) {
      existing.setData(countries);
    } else {
      map.addSource(COUNTRIES_SOURCE_ID, {
        type: 'geojson',
        data: countries,
      });
    }
    syncCountryBorderSource(map, countries);
    ensureCountryOverlayLayers(map);
    promoteCountryOverlayLayers(map);
    syncBasemapAdminBoundaries(map);
  } catch {
    /* style mid-swap */
  }
}

/**
 * Keep Manage-Country borders above interleaved deck.gl custom layers.
 * Prefer remove+re-add for the outline so it is guaranteed to be the
 * topmost style layer (moveLayer can lose a race with MapboxOverlay).
 */
export function promoteCountryOverlayLayers(map: MapboxMap): void {
  try {
    if (!map.getSource(COUNTRY_BORDERS_SOURCE_ID)) return;

    // Re-apply basemap admin visibility — style swaps / deck sync can reset it.
    // Country (admin-0) borders stay visible under the green/red overlay;
    // province (admin-1) lines stay hidden.
    syncBasemapAdminBoundaries(map);

    if (map.getLayer(COUNTRIES_OUTLINE_LAYER_ID)) {
      map.removeLayer(COUNTRIES_OUTLINE_LAYER_ID);
    }
    map.addLayer({
      id: COUNTRIES_OUTLINE_LAYER_ID,
      type: 'line',
      source: COUNTRY_BORDERS_SOURCE_ID,
      layout: COUNTRIES_OUTLINE_LAYOUT,
      paint: COUNTRIES_OUTLINE_PAINT as any,
    });

    // Hit-test fill just under the outline (not above it).
    if (map.getLayer(COUNTRIES_FILL_LAYER_ID)) {
      try {
        map.moveLayer(COUNTRIES_FILL_LAYER_ID, COUNTRIES_OUTLINE_LAYER_ID);
      } catch {
        /* ignore */
      }
    }
  } catch {
    /* style not ready / layer mid-swap */
  }
}

function applyPaint(map: MapboxMap, layerId: string, paint: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(paint)) {
    try {
      map.setPaintProperty(layerId, key as any, value as any);
    } catch {
      /* unsupported on this style / GL version */
    }
  }
}

export type CountryOverlayOptions = {
  /**
   * When false, skip the native Mapbox outline (main map draws borders via
   * deck.gl instead so interleaved overlay cannot hide/double them).
   * Dashboard keeps the default Mapbox outline.
   */
  mapOutline?: boolean;
};

/**
 * Ensure the countries source has fill (+ optional unique-arc outline).
 */
export function ensureCountryOverlayLayers(
  map: MapboxMap,
  opts: CountryOverlayOptions = {},
): void {
  if (!map.getSource(COUNTRIES_SOURCE_ID)) return;
  const mapOutline = opts.mapOutline !== false;

  if (!map.getSource(COUNTRY_BORDERS_SOURCE_ID)) {
    map.addSource(COUNTRY_BORDERS_SOURCE_ID, {
      type: 'geojson',
      data: EMPTY_FC,
    });
  }

  if (!map.getLayer(COUNTRIES_FILL_LAYER_ID)) {
    map.addLayer({
      id: COUNTRIES_FILL_LAYER_ID,
      type: 'fill',
      source: COUNTRIES_SOURCE_ID,
      paint: COUNTRIES_FILL_PAINT as any,
    });
  } else {
    applyPaint(map, COUNTRIES_FILL_LAYER_ID, COUNTRIES_FILL_PAINT);
  }

  // Strip any accidental line layers still attached to the polygon source
  // (legacy outline) — those are the classic shared-border doubles.
  try {
    const styleLayers = map.getStyle()?.layers ?? [];
    for (const layer of styleLayers) {
      if (
        layer.type === 'line'
        && (layer as { source?: string }).source === COUNTRIES_SOURCE_ID
      ) {
        map.removeLayer(layer.id);
      }
    }
  } catch {
    /* ignore */
  }

  if (!mapOutline) {
    if (map.getLayer(COUNTRIES_OUTLINE_LAYER_ID)) {
      try {
        map.removeLayer(COUNTRIES_OUTLINE_LAYER_ID);
      } catch {
        /* ignore */
      }
    }
    return;
  }

  // Always (re)place outline as the top layer — shared path for dashboard
  // and main (main needs this after interleaved deck.gl inserts itself).
  promoteCountryOverlayLayers(map);
}

/**
 * Show the basemap's own admin-0 (country) borders as thin grey geographic
 * lines beneath the green/red policy overlay, and hide admin-1 (province)
 * lines to avoid clutter. Same on main map and dashboard.
 *
 * The green/red overlay comes from the coarser Manage-Country data, so at high
 * zoom the crisp basemap border can peek slightly beside it — that's expected;
 * it gives the map real geographic borders instead of a blank canvas.
 */
export function syncBasemapAdminBoundaries(map: MapboxMap): void {
  try {
    const layers = map.getStyle()?.layers ?? [];
    for (const layer of layers) {
      const id = layer.id;
      const isCountry = id.includes('admin-0') || id.includes('admin0');
      const isProvince = id.includes('admin-1') || id.includes('admin1');
      if (!isCountry && !isProvince) continue;
      try {
        map.setLayoutProperty(
          id,
          'visibility',
          isCountry ? 'visible' : 'none',
        );
      } catch {
        /* layer gone */
      }
    }
  } catch {
    /* style not ready */
  }
}
